import {
  billingAddressInvalidResponseSchema,
  billingAddressResponseSchema,
  billingAddressUpdateRequestSchema,
  billingPublicConfigResponseSchema,
  changeIntervalRequestSchema,
  commonErrorCodeResponseSchema,
  notFoundResponseSchema,
  permissionDeniedResponseSchema,
  quoteSubscriptionInvalidResponseSchema,
  quoteSubscriptionResponseSchema,
  quoteSubscriptionRequestSchema,
  startSubscriptionRequestSchema,
  startSubscriptionResponseSchema,
  subscriptionOverviewResponseSchema,
  subscriptionUsageResponseSchema,
  vatPreviewRequestSchema,
  vatPreviewResponseSchema,
} from 'xosign-types';

import { z } from 'zod';
import { API_URL_BASE } from '@/config';
import { type EndpointDefinition } from '@/types/endpointTypes';

const endpoints = {
  'billing.public-config': {
    method: 'GET',
    url: `${API_URL_BASE}billing/public-config`,
    requestSchema: z.undefined(),
    responseSuccessSchema: billingPublicConfigResponseSchema,
    requiresToken: 'none',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof billingPublicConfigResponseSchema,
    z.ZodUndefined
  >,
  'subscription.billing.quote': {
    method: 'POST',
    url: `${API_URL_BASE}billing/quote-subscription`,
    requestSchema: quoteSubscriptionRequestSchema,
    responseSuccessSchema: quoteSubscriptionResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: quoteSubscriptionInvalidResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof quoteSubscriptionRequestSchema,
    typeof quoteSubscriptionResponseSchema,
    {
      400: typeof quoteSubscriptionInvalidResponseSchema;
    }
  >,
  'subscription.billing.vatPreview': {
    method: 'POST',
    url: `${API_URL_BASE}billing/vat-preview`,
    requestSchema: vatPreviewRequestSchema,
    responseSuccessSchema: vatPreviewResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    typeof vatPreviewRequestSchema,
    typeof vatPreviewResponseSchema,
    z.ZodUndefined
  >,
  'subscription.billing.address.get': {
    method: 'GET',
    url: `${API_URL_BASE}billing/address`,
    requestSchema: z.undefined(),
    responseSuccessSchema: billingAddressResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: billingAddressInvalidResponseSchema,
      404: notFoundResponseSchema,
    },
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof billingAddressResponseSchema,
    {
      400: typeof billingAddressInvalidResponseSchema;
      404: typeof notFoundResponseSchema;
    }
  >,
  'subscription.billing.address.update': {
    method: 'POST',
    url: `${API_URL_BASE}billing/address`,
    requestSchema: billingAddressUpdateRequestSchema,
    responseSuccessSchema: billingAddressResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: billingAddressInvalidResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof billingAddressUpdateRequestSchema,
    typeof billingAddressResponseSchema,
    {
      400: typeof billingAddressInvalidResponseSchema;
    }
  >,
  'subscription.me': {
    method: 'GET',
    url: `${API_URL_BASE}subscriptions/me`,
    requestSchema: z.undefined(),
    responseSuccessSchema: subscriptionOverviewResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof subscriptionOverviewResponseSchema,
    z.ZodUndefined
  >,
  'subscription.usage': {
    method: 'GET',
    url: `${API_URL_BASE}subscriptions/usage`,
    requestSchema: z.undefined(),
    responseSuccessSchema: subscriptionUsageResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      403: permissionDeniedResponseSchema,
    },
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof subscriptionUsageResponseSchema,
    { 403: typeof permissionDeniedResponseSchema }
  >,
  'subscription.start': {
    method: 'POST',
    url: `${API_URL_BASE}subscriptions/start`,
    requestSchema: startSubscriptionRequestSchema,
    responseSuccessSchema: startSubscriptionResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof startSubscriptionRequestSchema,
    typeof startSubscriptionResponseSchema,
    {
      400: typeof commonErrorCodeResponseSchema;
    }
  >,
  'subscription.sync': {
    method: 'POST',
    url: `${API_URL_BASE}subscriptions/sync`,
    requestSchema: z.undefined(),
    responseSuccessSchema: subscriptionOverviewResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: z.undefined(),
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof subscriptionOverviewResponseSchema,
    z.ZodUndefined
  >,
  'subscription.interval.change': {
    method: 'POST',
    url: `${API_URL_BASE}subscriptions/interval`,
    requestSchema: changeIntervalRequestSchema,
    responseSuccessSchema: subscriptionOverviewResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
    },
  } satisfies EndpointDefinition<
    typeof changeIntervalRequestSchema,
    typeof subscriptionOverviewResponseSchema,
    {
      400: typeof commonErrorCodeResponseSchema;
    }
  >,
  'subscription.interval.cancel': {
    method: 'DELETE',
    url: `${API_URL_BASE}subscriptions/interval`,
    requestSchema: z.undefined(),
    responseSuccessSchema: subscriptionOverviewResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
    },
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof subscriptionOverviewResponseSchema,
    {
      400: typeof commonErrorCodeResponseSchema;
    }
  >,
  'subscription.cancel': {
    method: 'POST',
    url: `${API_URL_BASE}subscriptions/cancel`,
    requestSchema: z.undefined(),
    responseSuccessSchema: subscriptionOverviewResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
    },
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof subscriptionOverviewResponseSchema,
    {
      400: typeof commonErrorCodeResponseSchema;
    }
  >,
  'subscription.restore': {
    method: 'POST',
    url: `${API_URL_BASE}subscriptions/restore`,
    requestSchema: z.undefined(),
    responseSuccessSchema: subscriptionOverviewResponseSchema,
    requiresToken: 'local',
    responseErrorSchemas: {
      400: commonErrorCodeResponseSchema,
    },
  } satisfies EndpointDefinition<
    z.ZodUndefined,
    typeof subscriptionOverviewResponseSchema,
    {
      400: typeof commonErrorCodeResponseSchema;
    }
  >,
};

export default endpoints;
